// 02A · Apport SCO — ce qu'on charge dans les monnayeurs ce matin.
//
// Les quatre caisses automatiques éditent à 23:55 un ticket « Vérification du
// tiroir » qui détaille leur contenu, dénomination par dénomination. L'extension
// ORIKA le dépose ; cet écran le lit, le contrôle et PROPOSE ce qu'il faut
// ajouter — au lieu que ça se calcule de tête devant chaque machine.
//
// Proposer, pas décider (P9). Chaque quantité est modifiable, et ce qui est
// retenu s'inscrit dans les apports du jour : c'est pour cela que l'étape passe
// AVANT l'étape 02, dont le papier d'apport reprend ces lignes.
//
// Tout tient dans un seul tableau : dénominations en lignes, machines en
// colonnes. La première version empilait quatre cartes pleine largeur — il
// fallait dérouler pour comparer deux machines, alors que la question du matin
// est justement « qu'est-ce que je prépare, pour les quatre ? ».
//
// Les contrôles du ticket sont refaits ici alors que l'extension les a déjà
// faits : le dépôt peut venir d'ailleurs, et un contrôle qui ne coûte rien ne se
// délègue pas à un artefact installé à la main sur un poste.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { useReglages } from '@/data/reglages'
import {
  MACHINES_DEFAUT, REGLES_SCO_DEFAUT, calculerApport, composerApport, controler, lireTicket,
  niveauCompte, nomMachine, ticketExploitable,
  type Ajustements, type ApportSco as Apport, type Controle, type Machine, type ReglesSco,
  type TicketTiroir,
} from '@/domain/sco'
import { fmtEur, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { Chip, Kpis, Lamp, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PropsEtape } from './types'

interface Lecture {
  id: string
  caisse: number | null
  fichier: string | null
  ticket: TicketTiroir | null
  controles: Controle[]
  erreur: string | null
}

interface Colonne {
  lecture: Lecture
  caisse: number
  nom: string
  apport: Apport | null
  echecs: Controle[]
  inscrit: boolean
}

const cle = (d: number) => d.toFixed(2)

const niveau = (l: { compte: number; nature: 'rouleau' | 'billet'; rechargeable: boolean },
                regles: ReglesSco) => niveauCompte(l.compte, l.nature, l.rechargeable, regles)

/**
 * Ce qui coiffe ou clôt une colonne s'aligne sur l'axe des CHAMPS, pas sur le
 * milieu de la cellule.
 *
 * Une cellule de saisie porte deux choses côte à côte — le compte lu, puis le
 * champ. Le couple est centré, donc le champ tombe à droite du milieu. Centrer
 * le nom de la machine, le total et le bouton sur ce même milieu les décalait
 * d'une trentaine de pixels vers la gauche : l'œil descend la colonne blanche
 * des champs et trouvait le total à côté. Le même faux-compte invisible remet
 * tout le monde sur la même verticale, quelle que soit la largeur du contenu.
 */
const SurAxe = ({ children }: { children: ReactNode }) => (
  <span className="sco-duo"><span className="sco-compte" aria-hidden="true" />{children}</span>
)

/** Ce que dit une couleur, en toutes lettres — l'infobulle du compte. */
const LEGENDE: Record<string, string> = {
  stop: 'sous le seuil, il faut recharger',
  warn: 'juste au-dessus du seuil, ça tiendra à peine',
  ok: 'stock confortable',
  nul: 'la machine ne recharge pas cette dénomination',
}

export default function ApportSco({ etape, lectureSeule }: PropsEtape) {
  const { date, journee, ecrireRapport } = useRun()
  const reglages = useReglages()
  const toast = useToast()

  const [lectures, setLectures] = useState<Lecture[]>([])
  const [chargement, setChargement] = useState(true)
  const [occupe, setOccupe] = useState<number | null>(null)
  const [detail, setDetail] = useState(false)

  // Les quantités reprises en main, par caisse puis par dénomination. Elles
  // vivent dans l'avancement de l'étape : ce sont des DÉCISIONS, elles doivent
  // survivre au rafraîchissement et se voir depuis un autre poste.
  const [ajuste, setAjuste] = useState<Record<string, Ajustements>>(
    () => (etape.avancement?.ajuste ?? {}) as Record<string, Ajustements>,
  )

  const machines: Machine[] = reglages.machinesSco.length > 0 ? reglages.machinesSco : MACHINES_DEFAUT
  const regles: ReglesSco = useMemo(() => {
    const r = reglages.reglesSco
    if (!r) return REGLES_SCO_DEFAUT
    return {
      seuilPieces: Number(r.seuil_pieces) || REGLES_SCO_DEFAUT.seuilPieces,
      seuilBillets: Number(r.seuil_billets) || REGLES_SCO_DEFAUT.seuilBillets,
      billetsInitial: Number(r.billets_initial) || REGLES_SCO_DEFAUT.billetsInitial,
      billetsPalier: Number(r.billets_palier) || REGLES_SCO_DEFAUT.billetsPalier,
      billetsRecharges: Array.isArray(r.billets_recharges) && r.billets_recharges.length > 0
        ? r.billets_recharges.map(Number) : REGLES_SCO_DEFAUT.billetsRecharges,
      vigilancePieces: Number(r.vigilance_pieces) || REGLES_SCO_DEFAUT.vigilancePieces,
      vigilanceBillets: Number(r.vigilance_billets) || REGLES_SCO_DEFAUT.vigilanceBillets,
    }
  }, [reglages.reglesSco])

  // ── Charger les dépôts et les lire ───────────────────────────────────────
  useEffect(() => {
    let vivant = true
    setChargement(true)
    void (async () => {
      try {
        const depots = (await api.listerImports(date))
          .filter((d) => d.type === 'verif_tiroir' && d.statut !== 'ignore')
        const lues = await Promise.all(depots.map(async (d): Promise<Lecture> => {
          const base = { id: d.id, fichier: d.fichier }
          try {
            const ticket = lireTicket(await api.telechargerJson(d.chemin))
            if (!ticket) {
              return { ...base, caisse: null, ticket: null, controles: [],
                       erreur: 'Dépôt illisible — ce n’est pas un ticket.' }
            }
            return { ...base, caisse: ticket.caisse, ticket,
                     controles: controler(ticket, date), erreur: null }
          } catch (e) {
            return { ...base, caisse: null, ticket: null, controles: [], erreur: messageErreur(e) }
          }
        }))
        if (!vivant) return
        lues.sort((a, b) => (a.caisse ?? 9999) - (b.caisse ?? 9999))
        setLectures(lues)
      } catch (e) {
        if (vivant) toast(messageErreur(e), true)
      } finally {
        if (vivant) setChargement(false)
      }
    })()
    return () => { vivant = false }
  }, [date, toast])

  // ── Ce que chaque colonne montre ─────────────────────────────────────────
  const inscrits = useMemo(
    () => (journee?.rapport?.apportsSco ?? {}) as Record<string, unknown>,
    [journee?.rapport],
  )

  const colonnes: Colonne[] = useMemo(() => lectures.map((l) => {
    const echecs = l.controles.filter((c) => !c.ok)
    const exploitable = l.ticket !== null && !l.erreur && ticketExploitable(l.controles)
    const caisse = l.caisse ?? -1
    return {
      lecture: l, caisse, nom: nomMachine(l.caisse, machines), echecs,
      // Un ticket qui échoue à un contrôle ne dicte RIEN : proposer un apport à
      // partir de chiffres douteux serait pire que se taire.
      apport: exploitable && l.ticket
        ? calculerApport(l.ticket, regles, ajuste[String(caisse)] ?? {})
        : null,
      inscrit: inscrits[String(caisse)] != null,
    }
  }), [lectures, machines, regles, ajuste, inscrits])

  const utiles = colonnes.filter((c) => c.apport !== null)
  const sansTicket = machines.filter((m) => !lectures.some((l) => l.caisse === m.caisse))
  const rejetees = colonnes.filter((c) => c.apport === null)
  const aFaire = utiles.filter((c) => (c.apport?.aRecharger ?? 0) > 0)
  const faites = aFaire.filter((c) => c.inscrit).length
  const totalGeneral = +utiles.reduce((s, c) => s + (c.apport?.valeur ?? 0), 0).toFixed(2)
  const ajustees = utiles.reduce((s, c) => s + (c.apport?.ajustees ?? 0), 0)

  // ── L'avancement, pour la lampe de la barre d'action ─────────────────────
  // `pret('02A')` ne peut pas le déduire du blob : la complétude de cette étape
  // vit dans fc_imports. On n'écrit QUE si le compte a bougé — `fc_etape_touch`
  // pousse aussi `derniere_activite`, qui pilote la reprise après interruption,
  // et un écran ouvert et oublié passerait pour du travail en cours.
  const posee = (etape.avancement?.sco ?? null) as Record<string, number> | undefined
  const etat = {
    attendues: machines.length, exploitables: utiles.length,
    aFaire: aFaire.length, faites,
  }
  const aJour = posee != null && (Object.keys(etat) as (keyof typeof etat)[])
    .every((k) => posee[k] === etat[k])
  useEffect(() => {
    if (chargement || lectureSeule || aJour) return
    void api.toucherEtape(date, etape.code, { sco: etat }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargement, lectureSeule, aJour])

  // ── Reprendre la main sur une quantité ───────────────────────────────────
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (minuteur.current) clearTimeout(minuteur.current) }, [])

  const poser = useCallback((caisse: number, denom: number, valeur: number) => {
    setAjuste((a) => {
      const pour = { ...(a[String(caisse)] ?? {}) }
      pour[cle(denom)] = Math.max(0, Math.round(valeur))
      const suite = { ...a, [String(caisse)]: pour }
      // La frappe est rapide, l'écriture ne doit pas la suivre coup par coup.
      if (minuteur.current) clearTimeout(minuteur.current)
      minuteur.current = setTimeout(() => {
        void api.toucherEtape(date, etape.code, { ajuste: suite }).catch(() => {})
      }, 500)
      return suite
    })
  }, [date, etape.code])

  const rendreAlgo = useCallback((caisse: number) => {
    setAjuste((a) => {
      const suite = { ...a }
      delete suite[String(caisse)]
      void api.toucherEtape(date, etape.code, { ajuste: suite }).catch(() => {})
      return suite
    })
  }, [date, etape.code])

  // ── Inscrire dans les apports du jour ────────────────────────────────────
  //
  // L'apport SCO vit sous `rapport.apportsSco`, PAS dans `rapport.apports` :
  // les monnayeurs reprennent des billets de 20 €, que le modèle d'apport
  // caissier ne connaît pas (il s'arrête à 10 €). Les mêler ferait sous-évaluer
  // tout le 20 € partout où la V2 relit le blob, et ferait apparaître « SCO 88 »
  // dans la liste des caissiers de la V2. L'étape 02 les affiche à côté des
  // caissiers, et le papier d'apport les imprime avec eux.
  const inscrire = useCallback(async (c: Colonne, retirer = false) => {
    if (lectureSeule || !c.apport || occupe !== null) return
    setOccupe(c.caisse)
    const suite = { ...inscrits }
    if (retirer) {
      delete suite[String(c.caisse)]
    } else {
      const { rouleaux, billets } = composerApport(c.apport)
      suite[String(c.caisse)] = {
        caisse: c.caisse,
        monnayeur: machines.find((m) => m.caisse === c.caisse)?.monnayeur ?? null,
        rouleaux, billets,
        valeur: c.apport.valeur,
        ajuste: c.apport.ajustees > 0,
        inscrit_at: new Date().toISOString(),
      }
    }
    try {
      await ecrireRapport(
        retirer ? `retrait de l’apport SCO ${c.caisse}` : `apport SCO ${c.caisse}`,
        { apportsSco: suite },
        () => api.patcherRapport(date, { apportsSco: suite }),
        etape.code,
      )
      toast(retirer
        ? `SCO ${c.caisse} RETIRÉ DES APPORTS`
        : `SCO ${c.caisse} INSCRIT — ${fmtEur(c.apport.valeur).toUpperCase()}`)
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      setOccupe(null)
    }
  }, [lectureSeule, occupe, inscrits, machines, ecrireRapport, date, etape.code, toast])

  if (chargement) return <Vide>Lecture des vérifications de tiroir…</Vide>

  if (lectures.length === 0) {
    return (
      <Notice c="warn">
        <b>Aucune vérification de tiroir déposée pour cette journée.</b> Les caisses automatiques
        éditent leur ticket à 23:55 ; l’extension ORIKA le dépose ensuite. Sans lui, rien ne permet
        de savoir ce qu’il faut recharger — l’étape est grisée d’office, et rouvrable si besoin.
      </Notice>
    )
  }

  // Les lignes du tableau : les dénominations rechargeables d'abord, le reste
  // seulement si on le demande. Un monnayeur accepte les gros billets mais ne
  // les distribue pas — ils n'ont rien à faire dans la vue de travail.
  const gabarit = colonnes.find((c) => c.apport)?.apport ?? null
  const lignes = (gabarit?.lignes ?? []).filter((l) => detail || l.rechargeable)

  return (
    <>
      <Kpis items={[
        { k: 'Machines lues', v: `${utiles.length} / ${machines.length}`,
          s: [
            sansTicket.length > 0 ? `${sansTicket.map((m) => `SCO ${m.caisse}`).join(' · ')} sans ticket` : '',
            rejetees.length > 0 ? `${rejetees.map((c) => `SCO ${c.caisse}`).join(' · ')} rejetée${rejetees.length > 1 ? 's' : ''}` : '',
          ].filter(Boolean).join(' · ') || 'toutes présentes',
          couleur: utiles.length < machines.length ? 'var(--warn)' : undefined },
        { k: 'Inscrit dans les apports', v: `${faites} / ${aFaire.length}`,
          s: aFaire.length === 0 ? 'rien à recharger' : undefined,
          couleur: aFaire.length > 0 && faites < aFaire.length ? 'var(--act)' : 'var(--ok)' },
        { k: 'Valeur à charger', v: fmtEur(totalGeneral) },
        { k: 'Quantités reprises', v: ajustees,
          s: ajustees > 0 ? 'écart avec la proposition' : 'proposition suivie',
          couleur: ajustees > 0 ? 'var(--act)' : undefined },
      ]} />

      {(sansTicket.length > 0 || rejetees.length > 0) && (
        <Notice c="warn">
          <b>{pluriel(sansTicket.length + rejetees.length, 'machine non lue', 'machines non lues')}</b>
          {sansTicket.length > 0 && <> — sans ticket : {sansTicket.map((m) => nomMachine(m.caisse, machines)).join(', ')}</>}
          {rejetees.length > 0 && <> — ticket rejeté : {rejetees.map((c) => c.nom).join(', ')}</>}.
          {' '}L’étape reste validable : ce qui manque est signalé, il faudra regarder la machine.
        </Notice>
      )}

      <div className="card">
        <div className="scrollx">
          <table className="t sco">
            <colgroup>
              <col style={{ width: 190 }} />
              {colonnes.map((c) => <col key={c.lecture.id} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="sco-lbl">
                  <span className="sco-lbl-in">
                    <span>Dénomination</span>
                    <button className="btn gh sm" onClick={() => setDetail((d) => !d)}>
                      {detail ? 'masquer' : 'tout voir'}
                    </button>
                  </span>
                </th>
                {colonnes.map((c) => (
                  <th key={c.lecture.id} className="sco-mach">
                    <SurAxe>
                      <span>
                        <Lamp c={c.apport === null ? 'stop' : c.inscrit ? 'ok' : (c.apport.aRecharger > 0 ? 'act' : 'ok')} />
                        <span className="sco-nom">SCO {c.caisse}</span>
                        {c.lecture.ticket?.heure && (
                          <span className="sco-h">{c.lecture.ticket.heure.slice(0, 5)}</span>
                        )}
                      </span>
                    </SurAxe>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {lignes.map((modele) => (
                <tr key={modele.denomination}>
                  <td className="sco-lbl">
                    <span className="sco-lbl-in">
                      <b>{modele.denomination < 1
                        ? `${Math.round(modele.denomination * 100)} c`
                        : `${modele.denomination} €`}</b>
                      <span className="sco-unite">
                        {modele.rechargeable
                          ? (modele.nature === 'rouleau' ? 'rouleaux' : 'billets')
                          : 'non rechargée'}
                      </span>
                    </span>
                  </td>
                  {colonnes.map((c) => {
                    const l = c.apport?.lignes.find((x) => x.denomination === modele.denomination)
                    if (!l) {
                      return (
                        <td key={c.lecture.id} className="sco-cell">
                          <span className="sco-duo">
                            <span className="sco-compte" />
                            <span className="sco-q sco-inerte">—</span>
                          </span>
                        </td>
                      )
                    }
                    return (
                      <td key={c.lecture.id} className={`sco-cell${l.quantite > 0 ? ' agir' : ''}`}>
                        <span className="sco-duo">
                          <span className="sco-compte" data-niv={niveau(l, regles) ?? undefined}
                                title={`dans la machine — ${LEGENDE[niveau(l, regles) ?? 'nul']}`}>
                            {l.compte}
                          </span>
                          {l.rechargeable ? (
                            <input
                              className="sco-q" type="number" min={0} inputMode="numeric"
                              disabled={lectureSeule || c.inscrit}
                              data-ajuste={l.ajuste ? 'true' : undefined}
                              value={l.quantite}
                              title={l.ajuste ? `proposition : ${l.propose}` : 'proposition de l’algorithme'}
                              onChange={(e) => {
                                const v = e.currentTarget.value
                                poser(c.caisse, l.denomination, v === '' ? 0 : Number(v))
                              }}
                            />
                          ) : <span className="sco-q sco-inerte">—</span>}
                        </span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="sco-tot">
                <td className="sco-lbl"><span className="sco-lbl-in">Valeur à charger</span></td>
                {colonnes.map((c) => (
                  <td key={c.lecture.id} className="sco-cell">
                    <SurAxe>
                      {c.apport
                        ? <b className="sco-somme">{fmtEur(c.apport.valeur)}</b>
                        : <span className="sco-inerte">rejeté</span>}
                    </SurAxe>
                  </td>
                ))}
              </tr>
              <tr className="sco-act">
                <td className="sco-lbl">
                  <span className="sco-lbl-in"><span className="hint">Total {fmtEur(totalGeneral)}</span></span>
                </td>
                {colonnes.map((c) => (
                  <td key={c.lecture.id} className="sco-cell">
                    <SurAxe>
                    {c.apport === null ? (
                      <Chip c="stop">REJETÉ</Chip>
                    ) : c.inscrit ? (
                      <button className="btn gh sm" disabled={lectureSeule}
                              data-encours={occupe === c.caisse ? 'true' : undefined}
                              onClick={() => void inscrire(c, true)}>
                        Inscrit ✓ retirer
                      </button>
                    ) : c.apport.aRecharger === 0 ? (
                      <Chip c="ok">RIEN À FAIRE</Chip>
                    ) : (
                      <div className="sco-boutons">
                        <button className="btn ok sm" disabled={lectureSeule}
                                data-encours={occupe === c.caisse ? 'true' : undefined}
                                onClick={() => void inscrire(c)}>
                          Inscrire
                        </button>
                        {c.apport.ajustees > 0 && (
                          <button className="btn gh sm" title="revenir à la proposition"
                                  disabled={lectureSeule} onClick={() => rendreAlgo(c.caisse)}>
                            ↺
                          </button>
                        )}
                      </div>
                    )}
                    </SurAxe>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {rejetees.length > 0 && (
        <div className="card" style={{ marginTop: 10 }}>
          {rejetees.map((c) => (
            <div className="li" key={c.lecture.id}>
              <Lamp c="stop" />
              <div className="body">
                <div className="t">{c.nom} — ce ticket ne peut pas dicter d’apport</div>
                <div className="s">
                  {c.lecture.erreur
                    ? c.lecture.erreur.toUpperCase()
                    : c.echecs.map((e) => `${e.libelle} — ${e.detail}`).join(' · ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="sco-legende">
        <span><i data-niv="stop" /> sous le seuil ({regles.seuilPieces} pièces / {regles.seuilBillets} billets)</span>
        <span><i data-niv="warn" /> jusqu’à {regles.vigilancePieces} pièces / {regles.vigilanceBillets} billets</span>
        <span><i data-niv="ok" /> au-delà</span>
      </div>

      <div className="hint" style={{ marginTop: 10 }}>
        La proposition suit les règles du poste : sous {regles.seuilPieces} pièces on ajoute un
        rouleau — autant que nécessaire pour repasser au-dessus — et sous {regles.seuilBillets}
        {' '}billets on en ajoute {regles.billetsInitial}, puis par paliers de {regles.billetsPalier}.
        Le petit chiffre de gauche est ce que contient la machine, coloré selon son niveau ; le
        champ est ce qu’on y met, et il se modifie. Ce qui est inscrit part dans les apports du
        jour et s’imprime avec eux.
      </div>
    </>
  )
}
